import React, { Component } from 'react';
import clsx from 'clsx';
import { Form, Field } from 'react-final-form';
import PropTypes from 'prop-types';
import Axios from 'axios/index';
import videoPropTypes from 'common/VideoPlayer/Proptypes';
import TTSButton from 'shared/TextToSpeechButton';
import I18n from 'i18n-js';
import ResetReadingGameBtn from '../ResetReadingGameBtn';
import AutoSave from './AutoSave';
import MultipleChoiceOption from './MultipleChoiceOption';
import QuestionLetter from './QuestionLetter';
import {
  formatTimestamp, renderImage, renderStatusMessage, renderVideo
} from './QuestionUtils';
import styles from './MultipleChoiceQuestion.module.scss';
import submit, { getPreviousAnswers } from './Questions/submit';
import SubmissionTextHelper from './SubmissionTextHelper';

export default class RadioGroupWithAutoSave extends Component {
  constructor(props) {
    super(props);

    this.state = {
      correctChoices: props.correctChoices,
      pointsEarned: (props.savedResult && parseFloat(props.savedResult.question_answer.score)) || 0,
      previousAnswers: getPreviousAnswers(this.props.savedResult),
      status: this.props.savedResult && this.props.savedResult.updated_at ? '200' : null,
      submitting: false,
      updatedAt: this.props.savedResult && formatTimestamp(this.props.savedResult.updated_at, false),
      value: this.props.savedResult && this.props.savedResult.answer
    };

    this.save = this.save.bind(this);
    this.setNewState = this.setNewState.bind(this);
  }

  setNewState(data) {
    this.setState(data);
  }

  // Append index of radio button to the given id
  getRadioButtonId(index) {
    return `${this.props.radioButtonId}_${index}`;
  }

  save = async (values) => {
    if (!this.props.submitForm) return;

    this.setState({ status: null, submitting: true });

    await Axios
      .post(this.props.ajaxUrl, { question_answer: values })
      .then((response) => {
        const data = response.data;
        if (data.errors) {
          this.setState({ status: '500' });
          console.log(data.errors);
        }
        else {
          const formattedDate = formatTimestamp(data.question_answer.updated_at, true);

          this.setState({
            status: '200',
            updatedAt: formattedDate,
            value: data.question_answer.answer
          });
        }
      })
      .catch((error) => {
        this.setState({ status: '400' });
        console.log(error);
      })
      .then(() => this.setState({ submitting: false }));
  };

  renderRadioButtons() {
    return this.props.options.map((option, index) => {
      const key = option.key;
      if (key === '') return null;

      const isAtMaxAttempts = this.state.previousAnswers.length >= this.props.maxAttempts;
      const hasCorrectAnswer = this.state.previousAnswers.length > 0 && this.state.pointsEarned === this.props.points;

      const isInPreviousAnswers = this.state.previousAnswers.includes(option.answer_value.toString());
      const isDisabled = this.props.disabled || isAtMaxAttempts || hasCorrectAnswer || isInPreviousAnswers;

      return (
        <li key={`${this.props.questionId}-${index}`}>
          <QuestionLetter index={index} />
          <Field
            key={`${this.props.radioButtonId}_${key}`}
            component={MultipleChoiceOption}
            disabled={isDisabled}
            id={this.getRadioButtonId(index)}
            label={key}
            name={this.props.radioButtonName}
            type="radio"
            value={option.answer_value.toString()}
          />
        </li>
      );
    });
  }

  renderTTSButton = () => {
    if (!this.props.questionTTSEnabled) return null;

    return (
      <TTSButton
        idToRead={`question_${this.props.questionId}`}
        locale={this.props.locale}
      />
    );
  };

  renderVideo = () => {
    if (this.props.isInReadingGame) return renderVideo({ minWidth: '550', video: this.props.video, width: '550' });

    return renderVideo({ video: this.props.video, width: '100%', minWidth: 'unset', maxWidth: '400' });
  };

  renderStimulus = () => (
    <div className={clsx('tw-mb-4', {
      [styles.stimuliWrapper]: this.props.isInReadingGame && this.props.isInSlide,
      'tw-justify-end': this.props.isInReadingGame && this.props.isInSlide,
    })}
    >
      {renderImage(this.props.image)}
      {renderImage(this.props.gif)}
      {this.renderVideo()}
    </div>
  );

  render() {
    const submissionTextHelper = new SubmissionTextHelper({
      correctChoices: this.state.correctChoices,
      disabled: this.props.disabled,
      hasSubmitted: this.state.previousAnswers.length > 0,
      maxAttempts: this.props.maxAttempts,
      options: this.props.options,
      pointsEarned: this.state.pointsEarned,
      previousAnswers: this.state.previousAnswers,
      submitting: this.state.submitting,
      value: this.state.value,
    });

    return (
      <Form
        keepDirtyOnReinitialize
        initialValues={{
          answer: this.state.value,
          question_id: this.props.questionId
        }}
        render={({ handleSubmit }) => (
          <>
            <form
              className={clsx({
                'has-image': !!this.props.image || !!this.props.gif || !!this.props.video,
                'reading-game-question': this.props.isInReadingGame,
                'slide-question': this.props.isInSlide,
              })}
              data-answered={!!this.state.value}
              data-question-type="MultipleChoiceQuestion"
              onSubmit={handleSubmit}
            >
              {!this.props.preview && <AutoSave debounce={this.props.saveDebounce} save={this.save} />}
              {this.props.stimulusPosition === 'above' && this.renderStimulus()}
              <fieldset id={`question_${this.props.questionId}`}>
                <legend
                  className="expandable_question question-toggle allow-list-style"
                  data-accordian-pair-id={this.props.questionId}
                >
                  <span
                    className="respect_breaks"
                    dangerouslySetInnerHTML={{ __html: this.props.legendText }}
                  />
                  {this.renderTTSButton()}
                  {!this.props.isInSlide &&
                    renderStatusMessage(this.state.status, this.state.submitting, this.state.updatedAt, 'Assessment')
                  }
                </legend>

                <div
                  className="js-question-details mb20"
                  data-accordian-pair-id={this.props.questionId}
                >
                  {this.props.stimulusPosition !== 'above' && this.renderStimulus()}
                  <ol className="mc-question-group-list upper-alpha">
                    {this.renderRadioButtons()}
                  </ol>
                </div>
              </fieldset>
            </form>
            {this.props.useSubmitButton && !this.props.preview && (
              <div className={clsx(styles.submitBtnContainer, 'submit-btn-container')}>
                <div className={styles.columnContainer}>
                  <div className={styles.submissionAlert}>
                    {submissionTextHelper.submissionAlert()}
                  </div>
                  <div className="tw-flex tw-gap-4 tw-items-center">
                    <button
                      className={`btn btn--purple ${styles.button}`}
                      disabled={submissionTextHelper.cannotSubmit()}
                      type="button"
                      onClick={() => (
                        submit({
                          ajaxUrl: this.props.ajaxUrl,
                          questionId: this.props.questionId,
                          setNewState: this.setNewState,
                          value: this.state.value,
                        })
                      )}
                    >
                      {I18n.t('submit')}
                    </button>
                    {this.props.useReadingGameResetBtn && this.props.readingGameId && (
                      <ResetReadingGameBtn
                        disabled={!this.state.value && this.state.previousAnswers.length === 0}
                        readingGameId={this.props.readingGameId}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        subscription={{}}
        onSubmit={this.save}
      />
    );
  }
}

RadioGroupWithAutoSave.propTypes = {
  ajaxUrl: PropTypes.string.isRequired,
  correctChoices: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  gif: PropTypes.shape({
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    width: PropTypes.number,
    zoomUrl: PropTypes.string,
  }),
  image: PropTypes.shape({
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    width: PropTypes.number,
    zoomUrl: PropTypes.string,
  }),
  isInReadingGame: PropTypes.bool,
  isInSlide: PropTypes.bool,
  legendText: PropTypes.string,
  locale: PropTypes.oneOf(['en', 'EN', 'es', 'ES']).isRequired,
  maxAttempts: PropTypes.number.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  points: PropTypes.number,
  preview: PropTypes.bool.isRequired,
  questionId: PropTypes.number.isRequired,
  questionTTSEnabled: PropTypes.bool,
  radioButtonId: PropTypes.string.isRequired,
  radioButtonName: PropTypes.string.isRequired,
  randomized: PropTypes.bool,
  readingGameId: PropTypes.number,
  saveDebounce: PropTypes.number,
  savedResult: PropTypes.shape({
    answer: PropTypes.string,
    correctChoices: PropTypes.arrayOf(PropTypes.string),
    previousAnswers: PropTypes.arrayOf(PropTypes.string),
    updated_at: PropTypes.string
  }),
  stimulusPosition: PropTypes.string,
  submitForm: PropTypes.bool,
  useReadingGameResetBtn: PropTypes.bool,
  useSubmitButton: PropTypes.bool,
  video: videoPropTypes,
};

RadioGroupWithAutoSave.defaultProps = {
  correctChoices: [],
  disabled: false,
  gif: null,
  image: null,
  isInReadingGame: false,
  isInSlide: false,
  legendText: '',
  points: 0,
  questionTTSEnabled: true,
  randomized: false,
  readingGameId: undefined,
  saveDebounce: 800,
  savedResult: null,
  stimulusPosition: 'below',
  submitForm: false,
  useReadingGameResetBtn: false,
  useSubmitButton: false,
  video: null,
};
